import { gql, useQuery } from '@apollo/client'
import { Price_Response } from 'shared/presenters/graphqlTypes'
import { SchedulablePresenter } from 'shared/presenters'
import { useFormikContext } from 'formik'
import { defined } from 'shared/presenters/presenter'
import { AddABookingValues } from 'helpers/generateInitialValuesForActivity'
import { formatChosenOptionalExtrasIntoInputs } from 'shared/helpers/formatOptionalExtraData/formatOptionalExtras'
import { useEnthusiast } from 'shared/state/useAuth'

interface UseGetPriceForBookingActivityProps {
  activity: SchedulablePresenter
}

type Prices = {
  [keys in
    | 'totalPrice'
    | 'finalPricePence'
    | 'adultsPrice'
    | 'childrenPrice']: number
}

export interface UseGetPriceForBookingActivity extends Prices {
  totalPriceLoading: boolean
}

/**
 * It runs a query to calculate the booking price.
 */
const useGetPriceForBookingActivity = ({
  activity
}: UseGetPriceForBookingActivityProps): UseGetPriceForBookingActivity => {
  const { enthusiast } = useEnthusiast()
  const {
    values: { adultsGoing, childrenGoing, optionalExtra }
  } = useFormikContext<AddABookingValues>()

  const chosenOptionalExtrasInputs =
    formatChosenOptionalExtrasIntoInputs(optionalExtra)

  const { loading, data } = useQuery<{
    Consumer_GetBookingPrice: Price_Response
  }>(GET_PRICE_FOR_BOOKING_ACTIVITY, {
    variables: {
      enthusiastEmail: enthusiast?.email,
      schedulableId: defined(activity.id),
      peopleGoing: adultsGoing + childrenGoing,
      childrenGoing,
      chosenOptionalExtras: chosenOptionalExtrasInputs
    }
  })

  const Consumer_GetBookingPrice = data?.Consumer_GetBookingPrice
  const totalPrice =
    Consumer_GetBookingPrice?.final_price_after_discount_pence ?? 0
  const finalPricePence = Consumer_GetBookingPrice?.final_price_pence ?? 0
  const adultsPrice =
    Consumer_GetBookingPrice?.price_per_adult_pence ??
    defined(activity.price_pence)
  const childrenPrice =
    Consumer_GetBookingPrice?.price_per_child_pence ??
    defined(activity.child_price_pence)

  return {
    totalPrice,
    adultsPrice,
    childrenPrice,
    finalPricePence,
    totalPriceLoading: loading
  }
}

export default useGetPriceForBookingActivity

const GET_PRICE_FOR_BOOKING_ACTIVITY = gql`
  query WidgetUseGetBookingPrice(
    $enthusiastEmail: String
    $schedulableId: ID!
    $chosenOptionalExtras: [chosen_optional_extra_input!]
    $peopleGoing: Int!
    $childrenGoing: Int!
  ) {
    Consumer_GetBookingPrice(
      enthusiast_email: $enthusiastEmail
      schedulable_id: $schedulableId
      chosen_optional_extras: $chosenOptionalExtras
      people_going: $peopleGoing
      children_going: $childrenGoing
    ) {
      chosen_optional_extras_total_price_pence
      final_price_pence
      group_discount_amount_pence
      price_per_adult_pence
      price_per_child_pence
      membership_discount_amount_pence
      final_price_after_discount_pence
    }
  }
`
